import { Fragment, type PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, SvgIcon, Tooltip, Typography, type SvgIconProps, type TooltipProps } from '@mui/material';

import type { LicenseHolder, Maker } from '@/api/domains/drugs.types';
import { ReactComponent as FactoryIcon } from '@/shared/assets/icons-2.0/factory-line.svg';
import { ReactComponent as LicenseHolderIcon } from '@/shared/assets/icons-2.0/license.svg';
import { formatMakerName } from '@/shared/helpers/format.helpers';

type LicenseHolderAndMakerInfoProps = PropsWithChildren &
  Pick<ItemTooltipProps, 'placement' | 'size'> & {
    licenseHolder?: Pick<LicenseHolder, 'country' | 'name'> | null;
    maker: Pick<Maker, 'name' | 'country'>;
  };

type ItemTooltipProps = Omit<TooltipProps, 'title' | 'children'> & {
  name: string;
  country: string;
  size?: SvgIconProps['fontSize'];
  slotProps?: {
    icon?: Omit<SvgIconProps, 'size'>;
  };
  tooltipType?: 'licenseHolder' | 'maker';
};

const ItemTooltip = (props: ItemTooltipProps) => {
  const { name, country, size = 'medium', slotProps, tooltipType, ...tooltipProps } = props;

  const isMaker = tooltipType === 'maker';
  const { t } = useTranslation('license-holder-and-maker-info');

  return (
    <Tooltip
      arrow
      title={
        <Fragment>
          <Typography component="div" variant="Body/small" color="neutral.500">
            {isMaker ? t('tooltip.maker') : t('tooltip.license-holder')}
          </Typography>
          <Typography variant="Body/small" color="neutral.100">
            {formatMakerName(name, country)}
          </Typography>
        </Fragment>
      }
      {...tooltipProps}
    >
      <Box component="span" display="inline-flex">
        <SvgIcon
          component={isMaker ? FactoryIcon : LicenseHolderIcon}
          fontSize={size}
          inheritViewBox
          sx={{ color: 'neutral.600' }}
          {...slotProps?.icon}
        />
      </Box>
    </Tooltip>
  );
};

export const LicenseHolderAndMakerInfo = (props: LicenseHolderAndMakerInfoProps) => {
  const { licenseHolder, maker, children, ...tooltipProps } = props;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {licenseHolder && (
        <ItemTooltip
          name={licenseHolder.name}
          country={licenseHolder.country}
          tooltipType="licenseHolder"
          {...tooltipProps}
        />
      )}
      <ItemTooltip name={maker.name} country={maker.country} tooltipType="maker" {...tooltipProps} />
      {children}
    </Stack>
  );
};
